/**
 * calc/priceBook.ts — THE price-resolution seam.
 *
 * Answers "what is the price of X on date D" for every engine in three layers:
 * composition (base < custom < warehouse, then priceLinks re-pointing), dated
 * forward-fill lookup (returns null BEFORE the first priced date — no phantom
 * value), and two pin-able "latest price" semantics (latestEntry as-is vs
 * latestClose finite-positive) that existed before this module and must not drift.
 *
 * Pure, no I/O.
 */
import { lastIndexOnOrBefore } from './dateIndex';
import { canonicalCurrency, fxRate, resolveTradeCurrency, type FxStore } from './fx';
import type { PriceStore, Trade } from './types';

/** One ticker's series: date ('YYYY-MM-DD') → close. */
export type PriceSeries = Record<string, number>;

/**
 * Overlay self-priced instrument series onto a base price store. Per ticker-date
 * the overlay wins. No-op (returns the SAME reference) when `custom` is empty,
 * so single-source portfolios are unaffected.
 */
export function mergeCustomPrices(
  base: PriceStore,
  custom?: PriceStore | null,
): PriceStore {
  if (!custom || Object.keys(custom).length === 0) return base;
  const out: PriceStore = { ...base };
  for (const [sym, series] of Object.entries(custom)) {
    out[sym] = { ...(out[sym] ?? {}), ...series };
  }
  return out;
}

/**
 * Re-point held symbols' prices to another symbol's series per settings.priceLinks
 * ({ heldSymbol: sourceSymbol }), when the source is present in the store. No-op
 * (same reference) when there are no links.
 */
export function applyPriceLinks(prices: PriceStore, links: Record<string, string>): PriceStore {
  if (!links || Object.keys(links).length === 0) return prices;
  const out: PriceStore = { ...prices };
  for (const [symbol, target] of Object.entries(links)) {
    if (target && out[target]) out[symbol] = out[target];
  }
  return out;
}

/**
 * The priceLinks that actually re-pointed a symbol in `book` — the same
 * "target present" condition applyPriceLinks applies — so a screen labels a
 * price "via <target>" only when it really is the target's series.
 */
export function appliedPriceLinks(book: PriceStore, links: Record<string, string> | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [symbol, target] of Object.entries(links ?? {})) {
    if (target && book[target]) out[symbol] = target;
  }
  return out;
}

/** The sources a price book is composed from. All layers optional but `base`. */
export interface PriceBookSources {
  /** The weakest layer: the shared view's quotes as one-point series (lib/quotesRoute.quotesAsBook); the app passes {}. */
  base: PriceStore;
  /** Statement-harvested self-priced series (ADR-0020). */
  custom?: PriceStore;
  /** EOD warehouse daily values for matched symbols (settings.warehouseLinks). */
  warehouse?: PriceStore;
  /** Held symbol → source symbol re-pointing (settings.priceLinks). */
  priceLinks?: Record<string, string>;
}

/**
 * THE composition rule for the price store: base < custom < warehouse, then
 * priceLinks re-pointing. Every data-intake hook composes through here, so a new
 * price source is a one-line change instead of an edit in every hook.
 */
export function composePriceBook(
  { base, custom = {}, warehouse = {}, priceLinks = {} }: PriceBookSources,
): PriceStore {
  return applyPriceLinks(
    mergeCustomPrices(mergeCustomPrices(base, custom), warehouse),
    priceLinks,
  );
}

/**
 * Held tickers the composed book cannot price: no series, or a series with no
 * dated close. The case this names (WORK.md item 45): a held EODHD code with
 * a QUOTE only (the `priceStore` blob, read until WORK.md item 52 step A2,
 * held nothing but quotes, which never reached the dated book), and the warehouse layer is
 * fetched only for `settings.warehouseLinks`, so a quote-only code with no
 * link and no custom series silently fell out of every valuation (and out of
 * the rebalance book, which then 422'd). `$`-prefixed pseudo-tickers are cash
 * and are never priced.
 */
export function unpricedHeld(book: PriceStore, held: Iterable<string>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const t of held) {
    if (!t || t.startsWith('$') || seen.has(t)) continue;
    seen.add(t);
    if (latestEntry(book[t]) === null) out.push(t);
  }
  return out;
}

/**
 * Fill ONLY the tickers the book cannot price from `fallback` (the warehouse
 * series fetched for `unpricedHeld`'s answer). A ticker the book already
 * prices is never touched — the fallback is a floor, not another layer that
 * outranks the configured ones. Returns the SAME reference when nothing is
 * added, so a fully priced book costs no re-render.
 */
export function fillUnpriced(book: PriceStore, fallback: PriceStore | null | undefined): PriceStore {
  let out: PriceStore | null = null;
  for (const [t, series] of Object.entries(fallback ?? {})) {
    if (latestEntry(book[t]) !== null || latestEntry(series) === null) continue;
    out = out ?? { ...book };
    out[t] = series;
  }
  return out ?? book;
}

/**
 * The unit each held code's trades are in: the resolved currency of its
 * EARLIEST buy or sell across every slice, an earlier-entered row winning a
 * tie — the unit `computeHoldings` opens the position in and the value curve
 * reads for the whole ticker (`equityCurve`'s first-seen rule). Cash
 * pseudo-tickers (`$`) are no holding.
 */
export function tradeUnits(slices: Iterable<Iterable<Trade>>): Record<string, string> {
  const first: Record<string, { date: string; unit: string }> = {};
  for (const slice of slices) {
    for (const t of slice) {
      if ((t.type !== 'BUY' && t.type !== 'SELL') || !t.ticker || t.ticker.startsWith('$')) continue;
      const seen = first[t.ticker];
      if (!seen || t.date < seen.date) first[t.ticker] = { date: t.date, unit: resolveTradeCurrency(t) };
    }
  }
  const out: Record<string, string> = {};
  for (const [ticker, { unit }] of Object.entries(first)) out[ticker] = unit;
  return out;
}

/**
 * Put each market series into the unit its holding was traded in (audit
 * MONEY-9). Every engine values `shares x close x rate(holding currency)`, so
 * a close must be in the holding's currency — but the market quotes 4,017 LSE
 * listings in pence (D1's `base_currency` GBX) while a trade inferred from
 * `.LSE`, or imported from a broker that writes pounds, is in GBP: a 72.50p
 * price read as £72.50, x100. `quoteUnits` is the quote currency the data
 * Worker answers per code (`/api/symbols/status`), `tradeUnits` the rule above.
 *
 * Within one major (GBX → GBP) every close is scaled by the exact factor; across
 * majors each date converts at that date's rate, and a date with no rate is
 * dropped, never kept in the wrong unit. A ticker with no quote unit, no trade
 * unit, the same unit, or in `keep` is untouched — `keep` names the series the
 * owner configured (a custom series, a warehouse or price link), which are in
 * the unit the owner chose and may be merged date by date with the market's,
 * so no one factor is right for them. Returns the SAME reference when nothing
 * changes.
 */
export function alignQuoteUnits(
  prices: PriceStore,
  quoteUnits: Readonly<Record<string, string>>,
  units: Readonly<Record<string, string>>,
  fxStore: FxStore,
  keep: ReadonlySet<string> = new Set(),
): PriceStore {
  let out: PriceStore | null = null;
  for (const [ticker, unit] of Object.entries(units)) {
    const series = prices[ticker];
    const from = canonicalCurrency(quoteUnits[ticker]);
    const to = canonicalCurrency(unit);
    if (!series || !from || !to || from === to || keep.has(ticker)) continue;
    // Same major: an exact factor, no series needed. Otherwise null, so each
    // date is converted at its own rate.
    const fixed = fxRate({}, from, to);
    const next: PriceSeries = {};
    for (const [date, px] of Object.entries(series)) {
      const rate = fixed ?? fxRate(fxStore, from, to, date);
      if (rate != null) next[date] = px * rate;
    }
    out = out ?? { ...prices };
    out[ticker] = next;
  }
  return out ?? prices;
}

/**
 * Forward-fill lookup: the close at the most recent date <= `target`, via binary
 * search over pre-sorted `sortedDates`. Returns null when `target` precedes the
 * first priced date — NO back-fill; a position is worth nothing before its series starts.
 */
export function lookupLastPriceOnOrBefore(
  sortedDates: string[],
  series: PriceSeries,
  target: string,
): number | null {
  const idx = lastIndexOnOrBefore(sortedDates, target);
  return idx === -1 ? null : series[sortedDates[idx]];
}

/**
 * The series' last dated entry — `{date, price}` at the greatest date key, AS-IS
 * (a 0 or NaN close is returned, not skipped). holdingsPnL's "current price"
 * semantic, pinned exactly; use `latestClose` when a finite/positive close is required.
 */
export function latestEntry(series: PriceSeries | undefined): { date: string; price: number } | null {
  if (!series) return null;
  const dates = Object.keys(series).sort();
  if (dates.length === 0) return null;
  const date = dates[dates.length - 1];
  return { date, price: series[date] };
}

/**
 * The series' last dated entry whose close is FINITE and POSITIVE, with its
 * date; null when none qualifies. A trailing 0 or NaN is a placeholder, not a
 * price: valuing a holding at it read a −100% loss and listed the holding as
 * a tax-loss-harvest candidate (audit MONEY-23). The date is the usable
 * close's, so a stale mark reads from it.
 */
export function latestUsableEntry(series: PriceSeries | undefined): { date: string; price: number } | null {
  if (!series) return null;
  let date = '';
  let price = NaN;
  for (const [d, px] of Object.entries(series)) {
    if (d > date && Number.isFinite(px) && px > 0) { date = d; price = px; }
  }
  return date ? { date, price } : null;
}

/**
 * The close at a ticker's most recent date holding a FINITE, POSITIVE value;
 * null when absent or no entry qualifies. Skips trailing 0/NaN placeholder closes.
 */
export function latestClose(book: PriceStore, ticker: string): number | null {
  const series = book[ticker];
  if (!series) return null;
  let bestDate = '';
  let bestPx = NaN;
  for (const [date, px] of Object.entries(series)) {
    if (date > bestDate && Number.isFinite(px) && px > 0) { bestDate = date; bestPx = px; }
  }
  return Number.isFinite(bestPx) ? bestPx : null;
}
